import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  CardMessageCode,
  IseeSessionQuery,
  TemplateType,
} from '@tronche2tek/shared-types';
import type { TFunction } from 'i18next';
import { useEffect, useState } from 'react';

import { msalInstance } from '../auth/msalConfig';
import type { CardProgress, SseHandle } from './cards-sse';
import { openCardStream } from './cards-sse';
import type {
  CardBatchDto,
  CardJobSummaryDto,
  CardPreflightRecapDto,
  CardPreviewUrlDto,
  CardsControllerGenerateFromIseeData,
  CardTemplateDto,
  CardTemplateSvgsDto,
} from './generated';
import {
  cardsControllerCancelRender,
  cardsControllerCommit,
  cardsControllerDiscardPreflight,
  cardsControllerDownload,
  cardsControllerGenerateFromIsee,
  cardsControllerGetJobs,
  cardsControllerHistory,
  cardsControllerHistoryDetail,
  cardsControllerHistoryDownload,
  cardsControllerPdf,
  cardsControllerPreflightCsv,
  cardsControllerPreflightLucca,
  cardsControllerPreview,
  cardsControllerPreviews,
  cardsControllerRecap,
  cardTemplateControllerGetTemplates,
  cardTemplateControllerGetTemplateSvgs,
} from './generated';

/** A per-card outcome that may carry a translatable code + fallback message. */
export interface CardMessageLike {
  message?: string;
  messageCode?: CardMessageCode;
  messageParams?: Record<string, string>;
}

/**
 * Translate a backend per-card message: prefer the stable `messageCode` (with
 * its interpolation params) so the text follows the UI language; fall back to
 * the raw `message` for arbitrary/upstream errors that have no code.
 */
export function cardMessageText(t: TFunction, m: CardMessageLike): string {
  if (m.messageCode) {
    return t(`cardErrors.${m.messageCode}`, { ...m.messageParams });
  }
  return m.message ?? '';
}

/** Recipient/data source for a card batch, driven by the template type. */
export type CardSource = 'csv' | 'isee' | 'lucca';

/**
 * Generation lifecycle phase shared by the card generators:
 * idle → uploading (POST pre-flight) → preflighting (resolve stream) → recap →
 * uploading (POST commit) → processing (render stream) → done | error.
 */
export type CardPhase =
  | 'idle'
  | 'uploading'
  | 'preflighting'
  | 'recap'
  | 'committing'
  | 'processing'
  | 'done'
  | 'error';

export type CardPreflightRecap = CardPreflightRecapDto;

/** Map a template type to the card-generation source it drives. */
export function sourceForType(type: TemplateType | undefined): CardSource {
  return type === 'student' ? 'isee' : type === 'employee' ? 'lucca' : 'csv';
}

/** Output format for the generated batch. */
export type OutputFormat = 'zip' | 'pdf';

/** Which card sides to generate; 'both' is the default and is never sent. */
export type CardSides = 'both' | 'front' | 'back';

/** The template fields the selected side(s) actually use. */
export function fieldsForSides(
  sides: CardSides,
  frontFields: string[],
  backFields: string[],
): string[] {
  if (sides === 'front') {
    return frontFields;
  }
  if (sides === 'back') {
    return backFields;
  }
  return [...new Set([...frontFields, ...backFields])];
}

/**
 * Discriminated input for {@link postPreflight}. Both generators share the
 * same three source branches; each provides only the fields its flow has (the
 * bulk page uploads a CSV file + delimiter, the single-card page builds a
 * one-row CSV `File`; ISEE takes filter fields in bulk vs an explicit login in
 * single). `values` carries the operator-entered `form:` fields.
 */
export type PreflightInput =
  | {
      source: 'lucca';
      templateId: string;
      logins: string[];
      outputFormat?: OutputFormat;
      sides?: CardSides;
      sortBy?: string;
      values?: Record<string, string>;
    }
  | {
      source: 'isee';
      templateId?: string;
      template?: Blob;
      /** The cohort picked in the sessions tree (bulk). */
      session?: IseeSessionQuery;
      /** Explicit logins (single-card / targeted), joined comma-separated. */
      logins?: string[];
      outputFormat?: OutputFormat;
      sides?: CardSides;
      sortBy?: string;
      values?: Record<string, string>;
    }
  | {
      source: 'csv';
      templateId?: string;
      template: Blob;
      csv: Blob;
      /** Matches what the backend accepts — typed here so no call site casts. */
      delimiter?: ',' | ';' | '\t' | '|';
      outputFormat?: OutputFormat;
      sides?: CardSides;
      /** Field the output is ordered by; omitted leaves the order alone. */
      sortBy?: string;
    };

function hasValues(
  values: Record<string, string> | undefined,
): values is Record<string, string> {
  return !!values && Object.keys(values).length > 0;
}

type IseePreflightBody = CardsControllerGenerateFromIseeData['body'];

/** POST a pre-flight for a source and return the `resolveBatchId`, throwing on failure. */
export async function postPreflight(input: PreflightInput): Promise<string> {
  if (input.source === 'lucca') {
    const { data } = await cardsControllerPreflightLucca({
      body: {
        templateId: input.templateId,
        logins: input.logins,
        ...(input.outputFormat ? { outputFormat: input.outputFormat } : {}),
        ...(input.sides && input.sides !== 'both'
          ? { sides: input.sides }
          : {}),
        ...(input.sortBy ? { sortBy: input.sortBy } : {}),
        ...(hasValues(input.values) ? { values: input.values } : {}),
      },
      throwOnError: true,
    });
    return data.resolveBatchId;
  }

  if (input.source === 'isee') {
    const body: IseePreflightBody = { ...input.session };
    if (input.logins?.length) {
      body.logins = input.logins.join(',');
    }
    if (input.templateId) {
      body.templateId = input.templateId;
    } else if (input.template) {
      body.template = input.template;
    }
    if (hasValues(input.values)) {
      body.values = JSON.stringify(input.values);
    }
    if (input.outputFormat) {
      body.outputFormat = input.outputFormat;
    }
    if (input.sides && input.sides !== 'both') {
      body.sides = input.sides;
    }
    if (input.sortBy) {
      body.sortBy = input.sortBy;
    }
    const { data } = await cardsControllerGenerateFromIsee({
      body,
      throwOnError: true,
    });
    return data.resolveBatchId;
  }

  const { data } = await cardsControllerPreflightCsv({
    body: {
      csv: input.csv,
      template: input.template,
      ...(input.templateId ? { templateId: input.templateId } : {}),
      ...(input.delimiter ? { delimiter: input.delimiter } : {}),
      ...(input.outputFormat ? { outputFormat: input.outputFormat } : {}),
      ...(input.sides && input.sides !== 'both' ? { sides: input.sides } : {}),
      ...(input.sortBy ? { sortBy: input.sortBy } : {}),
    },
    throwOnError: true,
  });
  return data.resolveBatchId;
}

export type CardBatch = CardBatchDto;
export type CardJobSummary = CardJobSummaryDto;

export type BatchProgress = CardProgress;

export const cardHistoryQueryOptions = () =>
  queryOptions({
    queryKey: ['cards', 'history'] as const,
    queryFn: async () => {
      const { data } = await cardsControllerHistory({ throwOnError: true });
      return data;
    },
    staleTime: 30_000,
  });

/**
 * The user's own batch lifecycle rows (resolve + render). Unlike
 * `/cards/history` (durable CardBatch rows, created at commit), this covers
 * pre-flight batches too — resolving, or awaiting review, resumable at
 * /cards?resume=<jobId>.
 */
export const cardJobsQueryOptions = () =>
  queryOptions({
    queryKey: ['cards', 'jobs'] as const,
    queryFn: async () => {
      const { data } = await cardsControllerGetJobs({ throwOnError: true });
      return data;
    },
    staleTime: 4_000,
  });

export const batchDetailQueryOptions = (batchId: string) =>
  queryOptions({
    queryKey: ['cards', 'batch', batchId] as const,
    queryFn: async () => {
      const { data } = await cardsControllerHistoryDetail({
        path: { batchId },
        throwOnError: true,
      });
      return data;
    },
    staleTime: 30_000,
  });

export type BatchProgressStatus =
  'idle' | 'connecting' | 'open' | 'retrying' | 'failed' | 'done';

/** Was this batch started by the signed-in user — the only one who can stream it? */
export function isOwnBatch(batch: Pick<CardBatch, 'actorOid'>): boolean {
  return batch.actorOid === msalInstance.getActiveAccount()?.localAccountId;
}

/**
 * Progress of a batch as the UI shows it: live over SSE for one's own batch,
 * from the polled history stats for anyone else's (admins see every batch,
 * but only the owner can attach to the progress stream — opening one got a
 * 404 and a false "connection lost").
 */
export function useBatchLiveProgress(batch: CardBatch | undefined): {
  data: BatchProgress | undefined;
  status: BatchProgressStatus;
} {
  const mine = batch !== undefined && isOwnBatch(batch);
  const live = useBatchProgress(batch && mine ? batch.jobId : null);
  if (!batch || mine) {
    return live;
  }
  const stats = batch.stats;
  return {
    data: {
      done:
        (stats?.success ?? 0) + (stats?.error ?? 0) + (stats?.notFound ?? 0),
      total: batch.rowCount,
    },
    status:
      batch.status === 'done'
        ? 'done'
        : batch.status === 'failed'
          ? 'failed'
          : 'open',
  };
}

export function useBatchProgress(jobId: string | null): {
  data: BatchProgress | undefined;
  status: BatchProgressStatus;
} {
  const queryClient = useQueryClient();
  // null = no event seen yet on the current stream; rendered as 'connecting'.
  const [streamStatus, setStreamStatus] = useState<BatchProgressStatus | null>(
    null,
  );

  // Reset stream status when the tracked job changes (set-during-render, so a
  // previous job's terminal status never leaks into the next one).
  const [prevJobId, setPrevJobId] = useState(jobId);
  if (jobId !== prevJobId) {
    setPrevJobId(jobId);
    setStreamStatus(null);
  }

  const query = useQuery({
    queryKey: ['batch-progress', jobId] as const,
    queryFn: () => ({ done: 0, total: 0 }) satisfies BatchProgress,
    enabled: jobId !== null,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (!jobId) {
      return;
    }

    const qk = ['batch-progress', jobId] as const;
    // Aborted on cleanup: closes the live stream and cancels an attempt still
    // waiting for its token, which esRef alone cannot reach.
    const ac = new AbortController();
    const esRef: { current: SseHandle | null } = { current: null };

    void openCardStream({
      path: `/cards/stream/${jobId}`,
      esRef,
      signal: ac.signal,
      onProgress: (data) => {
        queryClient.setQueryData(qk, data);
        setStreamStatus('open');
      },
      // A throttled batch can stay silent for minutes: 'open' comes from the
      // accepted stream, not from the first progress event.
      onOpen: () => setStreamStatus('open'),
      onReconnecting: () => setStreamStatus('retrying'),
      onDone: () => setStreamStatus('done'),
      onError: () => setStreamStatus('failed'),
    });

    return () => ac.abort();
  }, [jobId, queryClient]);

  const status: BatchProgressStatus =
    jobId === null ? 'idle' : (streamStatus ?? 'connecting');
  return { data: query.data, status };
}

/** Fetch the pre-flight recap once the resolve stream has emitted `done`. */
export async function getPreflightRecap(
  resolveBatchId: string,
): Promise<CardPreflightRecap> {
  const { data } = await cardsControllerRecap({
    path: { id: resolveBatchId },
    throwOnError: true,
  });
  return data;
}

/**
 * Discard a pre-flight batch the user walked away from (recap adjust/cancel):
 * clears its staged server state so it stops showing as awaiting review.
 */
export async function discardPreflight(resolveBatchId: string): Promise<void> {
  await cardsControllerDiscardPreflight({
    path: { id: resolveBatchId },
    throwOnError: true,
  });
}

/** Cancel a running render batch: queued cards are dropped, the row reads failed. */
export async function cancelRenderBatch(jobId: string): Promise<void> {
  await cardsControllerCancelRender({ path: { jobId }, throwOnError: true });
}

/** Commit a resolved batch — render only the validated recipients. */
export async function commitCards(
  resolveBatchId: string,
  outputFormat: 'zip' | 'pdf',
): Promise<{ jobId: string; deduplicated: boolean }> {
  const { data } = await cardsControllerCommit({
    body: { resolveBatchId, outputFormat },
    throwOnError: true,
  });
  return data;
}

export async function getCardDownloadUrl(
  jobId: string,
  format: OutputFormat = 'zip',
): Promise<string> {
  const call = format === 'pdf' ? cardsControllerPdf : cardsControllerDownload;
  const { data } = await call({ path: { jobId }, throwOnError: true });
  return data.url;
}

/**
 * Presigned preview URLs for a whole finished batch, keyed by upn — one call
 * for the results table (a request per card tripped the API's inbound rate
 * limit on large batches).
 */
export async function getCardPreviewUrls(
  jobId: string,
): Promise<Record<string, CardPreviewUrlDto>> {
  const { data } = await cardsControllerPreviews({
    path: { jobId },
    throwOnError: true,
  });
  return data.previews;
}

export async function getHistoryDownloadUrl(batchId: string): Promise<string> {
  const { data } = await cardsControllerHistoryDownload({
    path: { batchId },
    throwOnError: true,
  });
  return data.url;
}

// ── Card templates & preview ─────────────────────────────────────────────────

export type { CardTemplateDto };
export type CardTemplateSvgs = CardTemplateSvgsDto;

/** Stable identity while the list loads — a fresh `[]` per render would void
 *  every memo that depends on the template list. */
const NO_TEMPLATES: CardTemplateDto[] = [];

/** Saved card templates offered by the generators. */
export function useCardTemplates(): CardTemplateDto[] {
  const { data } = useQuery({
    queryKey: ['card-templates'],
    queryFn: async () => {
      const { data } = await cardTemplateControllerGetTemplates({
        throwOnError: true,
      });
      return data;
    },
    staleTime: 60_000,
  });
  return data ?? NO_TEMPLATES;
}

/** Fetch a template's SVGs + defaults (front, optional back). */
export async function getCardTemplateSvgs(
  id: string,
): Promise<CardTemplateSvgs> {
  const { data } = await cardTemplateControllerGetTemplateSvgs({
    path: { id },
    throwOnError: true,
  });
  return data;
}

export type CardPreview = CardPreviewUrlDto;

/** Presigned preview URLs for a finished card (front + optional back). */
export function useCardPreview(
  jobId: string | null,
  enabled: boolean,
  upn: string,
): CardPreview | undefined {
  const { data } = useQuery({
    queryKey: ['card-preview', jobId, upn],
    // `skipToken` narrows for us: the fetch only exists once the job does.
    queryFn:
      enabled && jobId
        ? async () => {
            const { data } = await cardsControllerPreview({
              path: { jobId, upn },
              throwOnError: true,
            });
            return data;
          }
        : skipToken,
    retry: false,
    staleTime: Infinity,
  });
  return data;
}
